/**
 * FrameJitterTracker (LRG.20) — rAF gap histogram + frame-phase
 * attribution for visual smoothness diagnosis.
 *
 * DJ's "core jitter on frame drawing" complaint sits BELOW the netcode
 * surface. Even with toggle on (smoothness wins), there's residual
 * frame-to-frame motion variance. We can't fix what we can't measure.
 *
 * What this captures (default-OFF; enable via tool checkbox or
 * `?framejitter=1` URL flag):
 *   - rAF gap histogram: last 120 frame deltas in ms. Buckets at 14/16/18/20/25/33+ ms.
 *   - p50 / p95 / p99 / max / min frame time over rolling 5s window.
 *   - Accumulator state: how much sub-frame leftover physics carries forward.
 *   - Spike attribution: when a frame > 25ms, capture the most recent phase
 *     timing (P2 predict, P6 reconcile, EventBus emits) to point at what ran long.
 *
 * Architecture:
 *   - Singleton with `enabled` flag (default OFF)
 *   - `tick(now)` called from the rAF callback in the game's main loop
 *     (we wire this into GameHost or EntitySystem.update's caller)
 *   - Ring buffer of last 120 frames
 *   - Stats computed lazily on `getStats()` — cheap on hot path
 *
 * UI surface: FrameJitterOverlay tool window with histogram + percentiles
 * + spike log. Reuses CopyButton pattern.
 *
 * Default OFF for zero overhead in production.
 */

import { browserOnly } from '../../shared/util/browserOnly';
import { gameLogger } from '../../utils/GameLogger';
import { bootSearchParams } from '../../config/boot/bootUrl';

export interface FrameSample {
  at: number;          // ms epoch
  deltaMs: number;     // gap since previous frame
  accumulator?: number; // physics accumulator value (set by EntitySystem)
  phase2Ms?: number;   // predict+sync phase wall time (if tracked)
}

export interface FrameJitterStats {
  enabled: boolean;
  sampleCount: number;
  windowMs: number;
  /** All in ms. */
  min: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
  mean: number;
  /** Bucketed counts. Bucket edges: <14, 14-16, 16-18, 18-20, 20-25, 25-33, 33+. */
  histogram: Array<{ bucket: string; count: number; threshold: number }>;
  /** Frames where deltaMs > 25ms (visible hitch). */
  spikesLast10s: number;
  recentSpikes: Array<{ at: number; deltaMs: number; phase2Ms?: number }>;
}

const RING_CAP = 120;            // ~2s @ 60Hz
const WINDOW_MS = 5_000;          // stats window
const SPIKE_THRESHOLD_MS = 25;    // frame > 25ms = visible hitch
const STORAGE_FLAG_NAME = 'framejitter';
const SPIKE_WARN_MIN_GAP_MS = 200; // throttle structured warn
const SAMPLE_WARMUP_MS = 2_000;   // discard samples in first 2s after enable
                                  // (cold-start frame at enable boundary
                                  //  can easily be >100ms; without this
                                  //  grace the server-side aggregator
                                  //  reports a fresh session as severe)

const BUCKETS: Array<{ label: string; lo: number; hi: number }> = [
  { label: '<14ms', lo: 0,   hi: 14 },
  { label: '14-16',  lo: 14,  hi: 16 },
  { label: '16-18',  lo: 16,  hi: 18 },
  { label: '18-20',  lo: 18,  hi: 20 },
  { label: '20-25',  lo: 20,  hi: 25 },
  { label: '25-33',  lo: 25,  hi: 33 },
  { label: '>33ms',  lo: 33,  hi: Infinity },
];

class FrameJitterTrackerImpl {
  enabled = false;
  private buffer: FrameSample[] = [];
  private lastFrameAt = 0;
  private lastSpikeWarnAt = 0;
  private enabledAt = 0;

  constructor() {
    browserOnly(() => {
      try {
        const sp = bootSearchParams();
        if (sp.get(STORAGE_FLAG_NAME) === '1') {
          this.enabled = true;
          this.enabledAt = (typeof performance !== 'undefined') ? performance.now() : Date.now();
        }
      } catch { /* ignore */ }
    });
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (v) {
      this.enabledAt = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    } else {
      this.lastFrameAt = 0;
      this.enabledAt = 0;
    }
  }

  /**
   * Called from the rAF callback. Computes delta from previous frame +
   * records the sample. Accepts optional phase timings (set by EntitySystem).
   */
  tick(now: number, opts?: { accumulator?: number; phase2Ms?: number }): void {
    if (!this.enabled) return;
    if (this.lastFrameAt === 0) {
      this.lastFrameAt = now;
      return;
    }
    const deltaMs = now - this.lastFrameAt;
    this.lastFrameAt = now;

    // Sample-warmup grace: discard samples for the first SAMPLE_WARMUP_MS
    // after enable. The very first measured frame post-enable often spans
    // a scene-mount / asset-load chunk and registers >100ms — that pushes
    // the server-side aggregator into "severe" even though the steady
    // state is healthy. Hard-coded 2s is enough to clear those without
    // hiding genuine post-warmup regressions.
    if (this.enabledAt > 0 && (now - this.enabledAt) < SAMPLE_WARMUP_MS) {
      return;
    }

    const sample: FrameSample = {
      at: now,
      deltaMs,
      accumulator: opts?.accumulator,
      phase2Ms: opts?.phase2Ms,
    };
    this.buffer.push(sample);
    if (this.buffer.length > RING_CAP) this.buffer.shift();

    if (deltaMs > SPIKE_THRESHOLD_MS) {
      // Throttled structured warn for MCP grep
      if (now - this.lastSpikeWarnAt > SPIKE_WARN_MIN_GAP_MS) {
        this.lastSpikeWarnAt = now;
        gameLogger.warn('frame-jitter', `[spike] ${deltaMs.toFixed(1)}ms frame (p2=${opts?.phase2Ms?.toFixed(1) ?? '?'})`, sample);
      }
    }
  }

  getStats(): FrameJitterStats {
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const cutoff = now - WINDOW_MS;
    const inWindow = this.buffer.filter(s => s.at > cutoff);
    if (inWindow.length === 0) {
      return {
        enabled: this.enabled,
        sampleCount: 0,
        windowMs: WINDOW_MS,
        min: 0, p50: 0, p95: 0, p99: 0, max: 0, mean: 0,
        histogram: BUCKETS.map(b => ({ bucket: b.label, count: 0, threshold: b.hi })),
        spikesLast10s: 0,
        recentSpikes: [],
      };
    }
    const sorted = inWindow.map(s => s.deltaMs).slice().sort((a, b) => a - b);
    const percentile = (p: number) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
    const sum = sorted.reduce((a, b) => a + b, 0);
    const histogram = BUCKETS.map(b => ({
      bucket: b.label,
      count: inWindow.filter(s => s.deltaMs >= b.lo && s.deltaMs < b.hi).length,
      threshold: b.hi,
    }));
    const cutoffSpikes = now - 10_000;
    const spikes = this.buffer.filter(s => s.at > cutoffSpikes && s.deltaMs > SPIKE_THRESHOLD_MS);

    return {
      enabled: this.enabled,
      sampleCount: inWindow.length,
      windowMs: WINDOW_MS,
      min: sorted[0],
      p50: percentile(0.5),
      p95: percentile(0.95),
      p99: percentile(0.99),
      max: sorted[sorted.length - 1],
      mean: sum / sorted.length,
      histogram,
      spikesLast10s: spikes.length,
      recentSpikes: spikes.slice(-10).map(s => ({
        at: s.at,
        deltaMs: s.deltaMs,
        phase2Ms: s.phase2Ms,
      })),
    };
  }

  clear(): void {
    this.buffer = [];
    this.lastFrameAt = 0;
  }

  /** LRG.27 — wire-shipped snapshot for the server-side aggregator.
   *  Mirrors the in-game tool's stats. Cheap (~50 bytes); cadence 5s. */
  getSnapshot(): import('../../shared/diagnostics/FrameJitterSnapshotTypes').FrameJitterSnapshotWire {
    const stats = this.getStats();
    const dominant = stats.histogram.reduce((best, b) => b.count > best.count ? b : best, stats.histogram[0]);
    return {
      capturedAt: Date.now(),
      enabled: stats.enabled,
      sampleCount: stats.sampleCount,
      windowMs: stats.windowMs,
      min: stats.min,
      mean: stats.mean,
      p50: stats.p50,
      p95: stats.p95,
      p99: stats.p99,
      max: stats.max,
      spikesLast10s: stats.spikesLast10s,
      dominantBucket: dominant?.bucket ?? '?',
      histogram: stats.histogram.map(b => ({
        bucket: b.bucket,
        count: b.count,
        threshold: Number.isFinite(b.threshold) ? b.threshold : null,
      })),
      recentSpikes: stats.recentSpikes.map(s => ({
        at: s.at,
        deltaMs: s.deltaMs,
        phase2Ms: s.phase2Ms,
      })),
    };
  }
}

export const FrameJitterTracker = new FrameJitterTrackerImpl();

if (typeof window !== 'undefined') {
  (window as any).__FRAME_JITTER_TRACKER__ = FrameJitterTracker;
}
